// Renders a structured research article (see src/constants/research.js).
// Styled to match the admin dashboard palette, since research is admin-only.
import React from 'react';
import styled from 'styled-components';
import { formatResearchDate } from '../constants/research';

const Wrapper = styled.article`
  color: #cbd5e0;
`;

const Title = styled.h2`
  font-size: 1.9rem;
  line-height: 1.3;
  color: #e2e8f0;
  margin: 0 0 12px 0;

  @media (max-width: 600px) {
    font-size: 1.4rem;
  }
`;

const Subheader = styled.p`
  font-size: 1.1rem;
  font-weight: 500;
  color: #63b3ed;
  margin: 0 0 16px 0;
`;

const Meta = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  font-size: 0.85rem;
  color: #718096;
  padding-bottom: 20px;
  border-bottom: 1px solid #2d3748;
`;

const Summary = styled.p`
  font-size: 1.08rem;
  line-height: 1.75;
  color: #e2e8f0;
  border-left: 3px solid #63b3ed;
  padding-left: 18px;
  margin: 26px 0;
`;

const SectionHeading = styled.h3`
  font-size: 1.4rem;
  color: #63b3ed;
  margin: 40px 0 16px 0;
  padding-bottom: 8px;
  border-bottom: 1px solid rgba(99, 179, 237, 0.25);
  line-height: 1.35;

  @media (max-width: 600px) {
    font-size: 1.2rem;
  }
`;

const SubHeading = styled.h4`
  font-size: 1.12rem;
  color: #e2e8f0;
  margin: 28px 0 10px 0;
`;

const Paragraph = styled.p`
  font-size: 1.03rem;
  line-height: 1.8;
  color: #cbd5e0;
  margin: 0 0 16px 0;
`;

const CiteRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin: -6px 0 18px 0;
`;

const CiteTag = styled.span`
  font-size: 0.7rem;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  color: #a0aec0;
  background: rgba(255, 255, 255, 0.04);
  border: 1px solid rgba(255, 255, 255, 0.09);
  border-radius: 4px;
  padding: 3px 8px;
`;

const SourceGrid = styled.div`
  display: grid;
  gap: 14px;
  margin: 22px 0 26px 0;
`;

const SourceCard = styled.div`
  background: rgba(45, 55, 72, 0.5);
  border: 1px solid rgba(74, 85, 104, 0.4);
  border-left: 3px solid #63b3ed;
  border-radius: 10px;
  padding: 18px 20px;

  .source-name {
    font-size: 1.02rem;
    font-weight: 700;
    color: #e2e8f0;
    margin-bottom: 3px;
  }

  .source-person {
    font-size: 0.85rem;
    color: #63b3ed;
    margin-bottom: 10px;
  }

  p {
    font-size: 0.98rem;
    line-height: 1.7;
    color: #a0aec0;
    margin: 0;
  }
`;

const TableWrapper = styled.div`
  overflow-x: auto;
  margin: 22px 0 26px 0;
  border: 1px solid rgba(74, 85, 104, 0.4);
  border-radius: 10px;
`;

const Table = styled.table`
  width: 100%;
  min-width: 520px;
  border-collapse: collapse;
  font-size: 0.95rem;

  th,
  td {
    text-align: left;
    padding: 12px 16px;
    border-bottom: 1px solid rgba(74, 85, 104, 0.35);
  }

  th {
    background: rgba(99, 179, 237, 0.1);
    color: #63b3ed;
    font-weight: 700;
    text-transform: uppercase;
    font-size: 0.75rem;
    letter-spacing: 0.06em;
  }

  td {
    color: #cbd5e0;
  }

  td:not(:first-child) {
    font-variant-numeric: tabular-nums;
  }

  tbody tr:last-child td {
    border-bottom: none;
  }
`;

const Quote = styled.blockquote`
  margin: 26px 0;
  padding: 20px 24px;
  background: rgba(99, 179, 237, 0.07);
  border-left: 3px solid #4fd1c5;
  border-radius: 0 10px 10px 0;

  p {
    font-size: 1.08rem;
    line-height: 1.7;
    color: #e2e8f0;
    font-style: italic;
    margin: 0 0 10px 0;
  }

  cite {
    font-size: 0.85rem;
    color: #718096;
    font-style: normal;
  }
`;

const Note = styled.aside`
  margin: 24px 0;
  padding: 18px 22px;
  background: rgba(236, 201, 75, 0.07);
  border: 1px solid rgba(236, 201, 75, 0.25);
  border-radius: 10px;

  .note-title {
    text-transform: uppercase;
    letter-spacing: 0.1em;
    font-size: 0.72rem;
    font-weight: 700;
    color: #ecc94b;
    margin-bottom: 8px;
  }

  p {
    font-size: 0.98rem;
    line-height: 1.7;
    color: #cbd5e0;
    margin: 0;
  }
`;

const BulletList = styled.ul`
  margin: 0 0 22px 0;
  padding: 0;
  list-style: none;

  li {
    position: relative;
    padding-left: 24px;
    margin-bottom: 14px;
    font-size: 1.01rem;
    line-height: 1.75;
    color: #cbd5e0;

    &::before {
      content: '→';
      position: absolute;
      left: 0;
      color: #63b3ed;
    }
  }
`;

const StepList = styled.ol`
  margin: 0 0 22px 0;
  padding: 0;
  list-style: none;
  counter-reset: research-step;

  li {
    counter-increment: research-step;
    position: relative;
    padding-left: 46px;
    margin-bottom: 22px;

    &::before {
      content: counter(research-step);
      position: absolute;
      left: 0;
      top: 0;
      width: 30px;
      height: 30px;
      display: flex;
      align-items: center;
      justify-content: center;
      border-radius: 50%;
      background: #63b3ed;
      color: #1a202c;
      font-weight: 700;
      font-size: 0.92rem;
    }

    .step-title {
      font-size: 1.06rem;
      font-weight: 700;
      color: #e2e8f0;
      margin-bottom: 6px;
    }

    p {
      font-size: 1rem;
      line-height: 1.75;
      color: #cbd5e0;
      margin: 0;
    }
  }

  @media (max-width: 600px) {
    li {
      padding-left: 40px;
    }
  }
`;

const ReferenceList = styled.ol`
  margin: 0;
  padding-left: 22px;

  li {
    font-size: 0.95rem;
    line-height: 1.7;
    color: #a0aec0;
    margin-bottom: 14px;

    a {
      color: #63b3ed;
      word-break: break-word;

      &:hover {
        color: #90cdf4;
      }
    }
  }
`;

const Citations = ({ cite }) => {
  if (!cite || cite.length === 0) return null;
  return (
    <CiteRow>
      {cite.map((source) => (
        <CiteTag key={source}>{source}</CiteTag>
      ))}
    </CiteRow>
  );
};

const renderBlock = (block, index) => {
  switch (block.type) {
    case 'h2':
      return <SectionHeading key={index}>{block.text}</SectionHeading>;

    case 'h3':
      return <SubHeading key={index}>{block.text}</SubHeading>;

    case 'p':
      return (
        <React.Fragment key={index}>
          <Paragraph>{block.text}</Paragraph>
          <Citations cite={block.cite} />
        </React.Fragment>
      );

    case 'sources':
      return (
        <SourceGrid key={index}>
          {block.items.map((item) => (
            <SourceCard key={item.name}>
              <div className="source-name">{item.name}</div>
              <div className="source-person">{item.person}</div>
              <p>{item.detail}</p>
              <Citations cite={item.cite} />
            </SourceCard>
          ))}
        </SourceGrid>
      );

    case 'table':
      return (
        <TableWrapper key={index}>
          <Table>
            <thead>
              <tr>
                {block.headers.map((header) => (
                  <th key={header}>{header}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {block.rows.map((row) => (
                <tr key={row[0]}>
                  {row.map((cell, cellIndex) => (
                    <td key={`${row[0]}-${cellIndex}`}>{cell}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </Table>
        </TableWrapper>
      );

    case 'quote':
      return (
        <Quote key={index}>
          <p>{block.text}</p>
          {block.attribution && <cite>{block.attribution}</cite>}
        </Quote>
      );

    case 'note':
      return (
        <Note key={index}>
          {block.title && <div className="note-title">{block.title}</div>}
          <p>{block.text}</p>
          <Citations cite={block.cite} />
        </Note>
      );

    case 'list':
      return (
        <BulletList key={index}>
          {block.items.map((item, itemIndex) => (
            <li key={itemIndex}>
              {item.text}
              <Citations cite={item.cite} />
            </li>
          ))}
        </BulletList>
      );

    case 'steps':
      return (
        <StepList key={index}>
          {block.items.map((item) => (
            <li key={item.title}>
              <div className="step-title">{item.title}</div>
              <p>{item.text}</p>
              <Citations cite={item.cite} />
            </li>
          ))}
        </StepList>
      );

    default:
      return null;
  }
};

const ResearchArticle = ({ article }) => {
  if (!article) return null;

  return (
    <Wrapper>
      <Title>{article.title}</Title>
      {article.subheader && <Subheader>{article.subheader}</Subheader>}
      <Meta>
        <span>{formatResearchDate(article.publishedAt)}</span>
        <span>&middot;</span>
        <span>{article.readingTime}</span>
        {article.tags && article.tags.length > 0 && (
          <>
            <span>&middot;</span>
            <span>{article.tags.join(', ')}</span>
          </>
        )}
      </Meta>

      {article.summary && <Summary>{article.summary}</Summary>}

      {article.blocks.map(renderBlock)}

      {article.references && article.references.length > 0 && (
        <>
          <SectionHeading>References</SectionHeading>
          <ReferenceList>
            {article.references.map((reference) => (
              <li key={reference.text}>
                {reference.text}
                {reference.url && (
                  <>
                    {' '}
                    <a href={reference.url} target="_blank" rel="noopener noreferrer">
                      {reference.url}
                    </a>
                  </>
                )}
              </li>
            ))}
          </ReferenceList>
        </>
      )}
    </Wrapper>
  );
};

export default ResearchArticle;
